import { FileRepository } from '~/repositories/fileRepository';
import { validateId } from '~/repositories/utils';
import { hoaServiceUrl } from '~/utils/env';
import { urlWithQuery } from '~/utils/url';
import type {
  BoardRequestDto,
  BoardRequestsPageDto,
  BoardRequestStatus,
  CreateBoardRequestDto,
  DecideBoardRequestDto,
  RequestAndVotingSummaryDto,
  UpdateDueAtDto,
} from '~/swagger/hoa/Api';

const BASE_URL = `${hoaServiceUrl()}/company-portal/associations/architectural-requests`;

export type ArchitecturalRequestListFilter = {
  currentPage?: number;
  pageSize?: number;
  statuses?: BoardRequestStatus[];
  propertyIds?: string[];
  isOverdue?: boolean;
};

export class ArchitecturalRequestRepository extends FileRepository {
  public async list(filter: ArchitecturalRequestListFilter): Promise<BoardRequestsPageDto> {
    const response = await this.fetchWithAuth(urlWithQuery(BASE_URL, filter));
    return this.getJsonResponse(response);
  }

  public async get(id: string): Promise<BoardRequestDto> {
    validateId(id, 'Architectural request');
    const response = await this.fetchWithAuth(`${BASE_URL}/${id}`);
    return this.getJsonResponse(response);
  }

  public async create(payload: CreateBoardRequestDto): Promise<BoardRequestDto> {
    const response = await this.fetchWithAuth(BASE_URL, {
      method: 'POST',
      body: JSON.stringify({ ...payload, files: payload.files ?? [] }),
    });
    return this.getJsonResponse(response);
  }

  public async forcePmReview(id: string): Promise<BoardRequestDto> {
    validateId(id, 'Architectural request');
    const response = await this.fetchWithAuth(`${BASE_URL}/${id}/force-pm-review`, { method: 'POST' });
    return this.getJsonResponse(response);
  }

  public async approve(id: string, payload: DecideBoardRequestDto): Promise<BoardRequestDto> {
    validateId(id, 'Architectural request');
    const response = await this.fetchWithAuth(`${BASE_URL}/${id}/approve`, { method: 'POST', body: JSON.stringify(payload) });
    return this.getJsonResponse(response);
  }

  public async deny(id: string, payload: DecideBoardRequestDto): Promise<BoardRequestDto> {
    validateId(id, 'Architectural request');
    const response = await this.fetchWithAuth(`${BASE_URL}/${id}/deny`, { method: 'POST', body: JSON.stringify(payload) });
    return this.getJsonResponse(response);
  }

  public async updateDueAt(id: string, payload: UpdateDueAtDto): Promise<BoardRequestDto> {
    validateId(id, 'Architectural request');
    const response = await this.fetchWithAuth(`${BASE_URL}/${id}/due-at`, { method: 'PUT', body: JSON.stringify(payload) });
    return this.getJsonResponse(response);
  }

  public async cancel(id: string): Promise<BoardRequestDto> {
    validateId(id, 'Architectural request');
    const response = await this.fetchWithAuth(`${BASE_URL}/${id}/cancel`, { method: 'POST' });
    return this.getJsonResponse(response);
  }

  public async getSummary(): Promise<RequestAndVotingSummaryDto> {
    const response = await this.fetchWithAuth(`${BASE_URL}/summary`);
    return this.getJsonResponse(response);
  }
}

export const architecturalRequestRepository = new ArchitecturalRequestRepository();
